import {
  Divider,
  Space,
  Switch,
  Typography,
  Spin,
  message,
  Button,
  InputNumber,
  Tooltip,
  Select,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import { withRouteChange } from '../../../services/APM';
import { aiPurple } from '../../../sinapse/constants/colors';
import { SparkIcon } from '../../../sinapse/icons/spark';

interface TenantConfigurationFormValues {
  enableIndeedIntegration: boolean;
  enableNetvagasIntegration: boolean;
  enableCareerjetIntegration: boolean;
  enableJoobleIntegration: boolean;
  enableSideMenu: boolean;
  enableAlchemerIntegration: boolean;
  alchemerTeamID?: number;
  enableExternalApiIntegration: boolean;
  enableGenerateJobpostingDescription: boolean;
  enableGenerateRejectionFeedback: boolean;
  t003_enableAutomaticEmailWhenCreateUser: boolean;
  t004_enableCentralEventJournal: boolean;
  enableCandidateResumeSummary: boolean;
  enableGenerateInterviewScript: boolean;
  enableFileProcessingWithSearchEngine: boolean;
  enableAttachmentSearchForTalentPool: boolean;
  showAttachmentContent: boolean;
  enableCompetencyGeneration: boolean;
  candidateResumeSummaryCallLimit: number;
  candidateResumeSummaryPlacement: string;
  enableGenerateScreeningQuestions: boolean;
  enableGeneratePracticalTechnicalCase: boolean;
  enableAuthSyncIntegration: boolean;
  enableNexaAssessment: boolean;
  t007_enableChangeLanguage: boolean;
  t008_enableWhatsAppSubscription: boolean;
  t010_enableNewApproverProfile: boolean;
  t011_enableExternalApiHiredCandidateComplement: boolean;
  t012_showJobPostingsAndOpportunitiesStats: boolean;
}

const TenantConfiguration = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm<TenantConfigurationFormValues>();

  const enableAlchemerIntegrationValue = Form.useWatch('enableAlchemerIntegration', form);
  const enableFileProcessingValue = Form.useWatch('enableFileProcessingWithSearchEngine', form);
  const enableCandidateResumeSummary = Form.useWatch('enableCandidateResumeSummary', form);
  const enableGenerateJobpostingDescription = Form.useWatch(
    'enableGenerateJobpostingDescription',
    form
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = useAPIMutation(
    async (values: TenantConfigurationFormValues, api) => {
      await Promise.all([
        api.patch('/configs/tenant-configurations/enable-indeed-integration/', {
          value: values.enableIndeedIntegration,
        }),
        api.patch('/configs/tenant-configurations/enable-netvagas-integration/', {
          value: values.enableNetvagasIntegration,
        }),
        api.patch('/configs/tenant-configurations/enable-careerjet-integration/', {
          value: values.enableCareerjetIntegration,
        }),
        api.patch('/configs/tenant-configurations/enable-jooble-integration/', {
          value: values.enableJoobleIntegration,
        }),
        api.patch('/configs/tenant-configurations/enable-side-menu/', {
          value: values.enableSideMenu,
        }),
        api.patch('/configs/tenant-configurations/enable-alchemer-integration/', {
          value: values.enableAlchemerIntegration,
        }),
        api.patch('/configs/tenant-configurations/alchemer-team/', {
          value: values.alchemerTeamID?.toString() || '',
        }),
        api.patch('/configs/tenant-configurations/enable-external-api-integration/', {
          value: values.enableExternalApiIntegration,
        }),
        api.patch('/configs/tenant-configurations/enable-generate-jobposting-description/', {
          value: values.enableGenerateJobpostingDescription,
        }),
        api.patch('/configs/tenant-configurations/enable-generate-rejection-feedback/', {
          value: values.enableGenerateRejectionFeedback,
        }),
        api.patch('/configs/tenant-configurations/enable-candidate-resume-summary/', {
          value: values.enableCandidateResumeSummary,
        }),
        api.patch('/configs/tenant-configurations/enable-automatic-email-when-create-user/', {
          value: values.t003_enableAutomaticEmailWhenCreateUser,
        }),
        api.patch('/configs/tenant-configurations/enable-central-event-journal/', {
          value: values.t004_enableCentralEventJournal,
        }),
        api.patch('/configs/tenant-configurations/enable-auth-sync-integration/', {
          value: values.enableAuthSyncIntegration,
        }),
        api.patch('/configs/tenant-configurations/enable-nexa-assessment/', {
          value: values.enableNexaAssessment,
        }),
        api.patch('/configs/tenant-configurations/enable-change-language/', {
          value: values.t007_enableChangeLanguage,
        }),
        api.patch('/configs/tenant-configurations/enable-generate-interview-script/', {
          value: values.enableGenerateInterviewScript,
        }),
        api.patch('/configs/tenant-configurations/enable_file_processing_with_search_engine/', {
          value: values.enableFileProcessingWithSearchEngine,
        }),
        api.patch('/configs/tenant-configurations/enable-whatsapp-subscription/', {
          value: values.t008_enableWhatsAppSubscription,
        }),
        api.patch('/configs/tenant-configurations/enable-new-approver-profile/', {
          value: values.t010_enableNewApproverProfile,
        }),
        api.patch(
          '/configs/tenant-configurations/enable-external-api-hired-candidate-complement/',
          {
            value: values.t011_enableExternalApiHiredCandidateComplement,
          }
        ),
        api.patch('/configs/tenant-configurations/show-job-postings-and-opportunities-stats/', {
          value: values.t012_showJobPostingsAndOpportunitiesStats,
        }),
      ]);

      // These patches should be executed after the previous ones to ensure the dependencies are met
      await api.patch('/configs/tenant-configurations/enable-competency-generation/', {
        value: values.enableCompetencyGeneration,
      });
      await api.patch('/configs/tenant-configurations/enable_attachment_search_for_talent_pool/', {
        value: values.enableAttachmentSearchForTalentPool,
      });
      await api.patch('/configs/tenant-configurations/show_attachment_content/', {
        value: values.showAttachmentContent,
      });

      if (values.enableCandidateResumeSummary) {
        await api.patch('/configs/tenant-configurations/candidate-resume-summary-call-limit/', {
          value: values.candidateResumeSummaryCallLimit,
        });
      }
      if (values.enableCandidateResumeSummary) {
        await api.patch('/configs/tenant-configurations/candidate-resume-summary-placement/', {
          value: values.candidateResumeSummaryPlacement,
        });
      }
      await api.patch('/configs/tenant-configurations/enable-generate-screening-questions/', {
        value: values.enableGenerateScreeningQuestions,
      });
      await api.patch('/configs/tenant-configurations/enable-generate-practical-technical-case/', {
        value: values.enableGeneratePracticalTechnicalCase,
      });
    },
    {
      onSuccess: () => {
        message.success(t('Configurações atualizadas.'));
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
      onError: (err, variables, context) => {
        const { onError } = queryClient.defaultMutationOptions();
        onError && onError(err, variables, context);

        form.resetFields();
      },
    }
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>
          {t('Configurações do Tenant | Área do CS')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Spin spinning={config.isLoading}>
        <Form<TenantConfigurationFormValues>
          form={form}
          layout="horizontal"
          initialValues={{
            enableIndeedIntegration: config.data?.enableIndeedIntegration,
            enableNetvagasIntegration: config.data?.enablePublicationNetvagasJobboard,
            enableCareerjetIntegration: config.data?.enableCareerjetIntegration,
            enableJoobleIntegration: config.data?.enableJoobleIntegration,
            enableSideMenu: config.data?.enableSideMenu,
            enableAlchemerIntegration: config.data?.enableAlchemerIntegration,
            alchemerTeamID: config.data?.alchemerTeamID,
            enableExternalApiIntegration: config.data?.enableExternalApiIntegration,
            enableGenerateJobpostingDescription: config.data?.enableGenerateJobpostingDescription,
            enableGenerateRejectionFeedback: config.data?.enableGenerateRejectionFeedback,
            enableCandidateResumeSummary: config.data?.enableCandidateResumeSummary,
            enableCompetencyGeneration: config.data?.enableCompetencyGeneration,
            t003_enableAutomaticEmailWhenCreateUser:
              config.data?.t003_enableAutomaticEmailWhenCreateUser,
            t004_enableCentralEventJournal: config.data?.t004_enableCentralEventJournal,
            enableGenerateInterviewScript: config.data?.enableGenerateInterviewScript,
            enableFileProcessingWithSearchEngine: config.data?.enableFileProcessingWithSearchEngine,
            enableAttachmentSearchForTalentPool: config.data?.enableAttachmentSearchForTalentPool,
            showAttachmentContent: config.data?.showAttachmentContent,
            candidateResumeSummaryCallLimit: config.data?.candidateResumeSummaryCallLimit ?? 10_000,
            candidateResumeSummaryPlacement: config.data?.candidateResumeSummaryPlacement,
            enableGenerateScreeningQuestions: config.data?.enableGenerateScreeningQuestions,
            enableGeneratePracticalTechnicalCase: config.data?.enableGeneratePracticalTechnicalCase,
            enableAuthSyncIntegration:
              config.data?.t001_enableCentralizedAuthIntegration &&
              config.data?.enableAuthSyncReplica,
            enableNexaAssessment: config.data?.enableNexaAssessment,
            t007_enableChangeLanguage: config.data?.t007_enableChangeLanguage,
            t008_enableWhatsAppSubscription: config.data?.t008_enableWhatsAppSubscription,
            t010_enableNewApproverProfile: config.data?.t010_enableNewApproverProfile,
            t011_enableExternalApiHiredCandidateComplement:
              config.data?.t011_enableExternalApiHiredCandidateComplement,
            t012_showJobPostingsAndOpportunitiesStats:
              config.data?.t012_showJobPostingsAndOpportunitiesStats,
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
        >
          <Form.Item>
            <Typography.Paragraph>
              <Trans>
                Configurações gerais do tenant, nas chaves abaixo você pode ativar ou desativar
                funcionalidades conforme o caso de uso e/ou preferência do cliente.
              </Trans>
            </Typography.Paragraph>
            <Space direction="vertical">
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Typography.Title level={5} type="secondary">
                    <Trans>Funcionalidade</Trans>
                  </Typography.Title>
                </Form.Col>
                <Form.Col flex="auto">
                  <Typography.Title level={5} type="secondary">
                    <Trans>Status</Trans>
                  </Typography.Title>
                </Form.Col>
              </Form.Row>
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Integração com Indeed</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableIndeedIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Integração Netvagas</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableNetvagasIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Integração CareerJet</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableCareerjetIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Integração Jooble</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableJoobleIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Menu Lateral Multiprodutos</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableSideMenu"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row justify="space-around" align="middle">
                <Form.Col span={8}>
                  <Trans>Testes Customizados</Trans>
                </Form.Col>
                <Form.Col span={4}>
                  <Form.Item
                    name="enableAlchemerIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="alchemerTeamID"
                    label={t('Team ID Alchemer')}
                    style={{ marginBottom: 0, width: '100%' }}
                    rules={[
                      {
                        required: enableAlchemerIntegrationValue,
                        message: 'Por favor, insira o Team ID.',
                      },
                    ]}
                  >
                    <InputNumber
                      controls={false}
                      min={1}
                      disabled={!enableAlchemerIntegrationValue}
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Integração API Externa</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableExternalApiIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>Envio automático de e-mail ao criar usuário</Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t003_enableAutomaticEmailWhenCreateUser"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Envio de eventos de vagas para a Central de Vagas</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t004_enableCentralEventJournal"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Integração de Usuários com Controle</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableAuthSyncIntegration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Teste de Potencial de Liderança</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableNexaAssessment"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Mudança de Idioma</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t007_enableChangeLanguage"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Busca no conteúdo dos currículos</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Tooltip
                    title={
                      !config.data?.enableAntivirusVerification
                        ? t(
                            'Ative a verificação de arquivos para habilitar a busca por conteúdo dos currículos.'
                          )
                        : undefined
                    }
                    placement="topLeft"
                  >
                    <Form.Item
                      name="enableFileProcessingWithSearchEngine"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch
                        disabled={!config.data?.enableAntivirusVerification}
                        onChange={(checked) => {
                          if (!checked) {
                            form.setFieldValue('enableAttachmentSearchForTalentPool', false);
                            form.setFieldValue('showAttachmentContent', false);
                          }
                        }}
                      />
                    </Form.Item>
                  </Tooltip>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Busca no conteúdo dos currículos no Banco de Talentos</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Tooltip
                    title={
                      !enableFileProcessingValue
                        ? t('Ative "Busca por conteúdo dos currículos" para habilitar esta opção.')
                        : undefined
                    }
                    placement="topLeft"
                  >
                    <Form.Item
                      name="enableAttachmentSearchForTalentPool"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch disabled={!enableFileProcessingValue} />
                    </Form.Item>
                  </Tooltip>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Exibir conteúdo extraído dos currículos</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Tooltip
                    title={
                      !enableFileProcessingValue
                        ? t('Ative "Busca por conteúdo dos currículos" para habilitar esta opção.')
                        : undefined
                    }
                    placement="topLeft"
                  >
                    <Form.Item
                      name="showAttachmentContent"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch disabled={!enableFileProcessingValue} />
                    </Form.Item>
                  </Tooltip>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Inscrição por WhatsApp</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t008_enableWhatsAppSubscription"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Novo Perfil Aprovador</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t010_enableNewApproverProfile"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Complemento de candidatos contratados na API externa</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t011_enableExternalApiHiredCandidateComplement"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <Trans>Exibir estatísticas de vagas e oportunidades</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="t012_showJobPostingsAndOpportunitiesStats"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Gerar descrição de vaga com a Synapses</Trans>{' '}
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableGenerateJobpostingDescription"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch
                      onChange={(checked) => {
                        if (!checked) {
                          form.setFieldValue('enableCompetencyGeneration', false);
                        }
                      }}
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Competências da vaga com a Synapses</Trans>
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableCompetencyGeneration"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch disabled={!enableGenerateJobpostingDescription} />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Gerar feedback de reprovação de candidato com a Synapses</Trans>{' '}
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableGenerateRejectionFeedback"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Gerar resumo de currículo com a Synapses</Trans>{' '}
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableCandidateResumeSummary"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Gerar perguntas de screening com a Synapses</Trans>{' '}
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableGenerateScreeningQuestions"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Gerar roteiro de entrevista com a Synapses</Trans>{' '}
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableGenerateInterviewScript"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={8}>
                  <SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />
                  <Trans>Gerar roteiro de case prático ou técnico com a Synapses</Trans>{' '}
                </Form.Col>
                <Form.Col flex="auto">
                  <Form.Item
                    name="enableGeneratePracticalTechnicalCase"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row justify="space-around" align="middle">
                <Form.Col span={12}>
                  <Form.Item
                    name="candidateResumeSummaryCallLimit"
                    label={t('Limite de chamadas de resumo de currículo')}
                    style={{ marginBottom: 0, width: '100%' }}
                    rules={[
                      {
                        required: enableCandidateResumeSummary,
                        message: t(
                          'Por favor, insira o limite de chamadas de resumo de currículo.'
                        ),
                      },
                    ]}
                    required
                  >
                    <InputNumber<string | number>
                      controls={false}
                      min={0}
                      disabled={!enableCandidateResumeSummary}
                      style={{ width: '60%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      parser={(displayValue) => {
                        if (!displayValue) {
                          return '';
                        }

                        return parseInt(displayValue?.replace(/\$\s?|(\.*)/g, '') || '0', 10);
                      }}
                    />
                  </Form.Item>
                </Form.Col>
                <Form.Col flex="auto">
                  <Typography.Text>
                    <Trans>Quantidade de chamadas de resumo de currículo atual:</Trans>{' '}
                    {config.data?.candidateResumeSummaryCounter}
                  </Typography.Text>
                </Form.Col>
              </Form.Row>
              <Divider style={{ margin: 0 }} />
              <Form.Row align="middle">
                <Form.Col span={13}>
                  <Form.Item
                    name="candidateResumeSummaryPlacement"
                    label={t('Local de exibição do resumo do currículo')}
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: enableCandidateResumeSummary,
                        message: t(
                          'Por favor, selecione o local onde o resumo do currículo deve ficar.'
                        ),
                      },
                    ]}
                    required
                  >
                    <Select
                      disabled={!enableCandidateResumeSummary}
                      options={[
                        {
                          key: 'CANDIDATE_ATTACHMENT',
                          value: 'CANDIDATE_ATTACHMENT',
                          label: t('Aba de Anexo'),
                        },
                        {
                          key: 'CANDIDATE_DETAILS',
                          value: 'CANDIDATE_DETAILS',
                          label: t('Aba de Dados'),
                        },
                        {
                          key: 'BOTH_TABS',
                          value: 'BOTH_TABS',
                          label: t('Abas de Dados e Anexo'),
                        },
                      ]}
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
            </Space>
          </Form.Item>
          <Form.Footer>
            <Form.FooterEnd>
              <Form.Item noStyle shouldUpdate={() => true}>
                {(form) => (
                  <Button onClick={() => form.resetFields()}>
                    <Trans>Cancelar</Trans>
                  </Button>
                )}
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={mutation.isLoading}>
                <Trans>Salvar</Trans>
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(TenantConfiguration);
